import { showToast } from '@utils/toastr';

/**
 * Show toast error messages (default behavior)
 * @param {Array} errorList - Array of error objects from jQuery validation
 */
const showToastErrors = errorList => {
    if (errorList.length > 0) {
        const errors = [];
        errorList.forEach(error => {
            errors.push(error.message);
        });
        showToast(errors.join('\n'), 'error');
    }
};

/**
 * Show field errors under form fields
 * @param {jQuery} form - The form element
 * @param {Array} errorList - Array of error objects from jQuery validation
 * @param {Object} schema - Validation schema containing errorField mappings
 * @param {Object} errorDisplayConfig - Error display configuration
 * @param {boolean} clearAll - Whether to clear all existing errors first (default: true)
 */
const showFieldErrors = (form, errorList, schema, errorDisplayConfig = {}, clearAll = true) => {
    if (!errorList || errorList.length === 0) {
        return;
    }

    // Only clear all existing errors if this is a full form validation
    if (clearAll) {
        form.find('.error-message').addClass('hidden').text('');
        form.find('input, textarea, select').removeClass('error border-red-500 focus:ring-red-500 focus:border-red-500');
    }

    errorList.forEach(error => {
        const fieldName = error.element.name;
        const errorMessage = error.message;

        // Get error field ID from schema or custom mapping
        let errorFieldId = null;

        // Try custom field mapping first
        if (errorDisplayConfig.fieldMapping && errorDisplayConfig.fieldMapping[fieldName]) {
            errorFieldId = errorDisplayConfig.fieldMapping[fieldName];
        } else if (schema?.rules?.[fieldName]?.errorField) {
            // Then try schema errorField
            errorFieldId = schema.rules[fieldName].errorField;
        } else {
            // Finally try default mapping pattern
            errorFieldId = `${fieldName}Error`;
        }

        if (errorFieldId) {
            const errorDiv = form.find(`#${errorFieldId}`);
            if (errorDiv.length) {
                errorDiv.text(errorMessage).removeClass('hidden');

                // Check if field should skip error highlight
                const skipHighlight = schema?.rules?.[fieldName]?.skipErrorHighlight === true;

                // Add error styling to the input field only if not skipped
                if (!skipHighlight) {
                    const inputField = form.find(`[name="${fieldName}"]`);
                    if (inputField.length) {
                        inputField.addClass('error border-red-500 focus:ring-red-500 focus:border-red-500');
                    }
                }
            }
        }
    });
};

/**
 * Validate a single field
 * @param {jQuery} form - The form element
 * @param {string} fieldName - The field name to validate
 * @param {Object} validator - jQuery validator instance
 * @param {Object} schema - Validation schema containing errorField mappings
 * @param {Object} errorDisplayConfig - Error display configuration
 */
const validateSingleField = (form, fieldName, validator, schema, errorDisplayConfig = {}) => {
    const fieldElement = form.find(`[name="${fieldName}"]`);
    if (fieldElement.length && validator) {
        // Clear previous error first
        clearFieldError(form, fieldName, schema, errorDisplayConfig);

        // Validate the field
        const isValid = validator.element(fieldElement[0]);

        if (!isValid) {
            // Get the error message for this field
            const errors = validator.errorList.filter(error => error.element.name === fieldName);
            if (errors.length > 0) {
                // Show error for this specific field only (preserves other field errors)
                showSingleFieldError(form, fieldName, errors[0].message, schema, errorDisplayConfig);
            }
        }

        return isValid;
    }
    return true;
};

/**
 * Show field errors selectively (only update/clear errors for fields in errorList)
 * This preserves errors for fields that are not currently being validated
 * @param {jQuery} form - The form element
 * @param {Array} errorList - Array of error objects from jQuery validation
 * @param {Object} schema - Validation schema containing errorField mappings
 * @param {Object} errorDisplayConfig - Error display configuration
 */
const showFieldErrorsSelectively = (form, errorList, schema, errorDisplayConfig = {}) => {
    if (!errorList || errorList.length === 0) {
        return;
    }

    // Get all fields that are currently being validated (have errors)
    const fieldsBeingValidated = errorList.map(error => error.element.name);

    // First, clear errors ONLY for fields that are currently being validated
    fieldsBeingValidated.forEach(fieldName => {
        clearFieldError(form, fieldName, schema, errorDisplayConfig);
    });

    // Then show new errors for these fields
    errorList.forEach(error => {
        const fieldName = error.element.name;
        const errorMessage = error.message;
        showSingleFieldError(form, fieldName, errorMessage, schema, errorDisplayConfig);
    });
};

/**
 * Show error for a specific field only (preserves other field errors)
 * @param {jQuery} form - The form element
 * @param {string} fieldName - The field name to show error for
 * @param {string} errorMessage - The error message
 * @param {Object} schema - Validation schema containing errorField mappings
 * @param {Object} errorDisplayConfig - Error display configuration
 */
const showSingleFieldError = (form, fieldName, errorMessage, schema, errorDisplayConfig = {}) => {
    // Get error field ID using same logic as showFieldErrors
    let errorFieldId = null;

    if (errorDisplayConfig.fieldMapping && errorDisplayConfig.fieldMapping[fieldName]) {
        errorFieldId = errorDisplayConfig.fieldMapping[fieldName];
    } else if (schema?.rules?.[fieldName]?.errorField) {
        errorFieldId = schema.rules[fieldName].errorField;
    } else {
        errorFieldId = `${fieldName}Error`;
    }

    if (errorFieldId) {
        const errorDiv = form.find(`#${errorFieldId}`);
        if (errorDiv.length) {
            errorDiv.text(errorMessage).removeClass('hidden');
        }
    }

    // Check if field should skip error highlight
    const skipHighlight = schema?.rules?.[fieldName]?.skipErrorHighlight === true;

    // Add error styling to the input field only if not skipped
    if (!skipHighlight) {
        const inputField = form.find(`[name="${fieldName}"]`);
        if (inputField.length) {
            inputField.addClass('error border-red-500 focus:ring-red-500 focus:border-red-500');
        }
    }
};

/**
 * Clear field error for a specific field
 * @param {jQuery} form - The form element
 * @param {string} fieldName - The field name to clear error for
 * @param {Object} schema - Validation schema containing errorField mappings
 * @param {Object} errorDisplayConfig - Error display configuration
 */
const clearFieldError = (form, fieldName, schema, errorDisplayConfig = {}) => {
    // Get error field ID using same logic as showFieldErrors
    let errorFieldId = null;

    if (errorDisplayConfig.fieldMapping && errorDisplayConfig.fieldMapping[fieldName]) {
        errorFieldId = errorDisplayConfig.fieldMapping[fieldName];
    } else if (schema?.rules?.[fieldName]?.errorField) {
        errorFieldId = schema.rules[fieldName].errorField;
    } else {
        errorFieldId = `${fieldName}Error`;
    }

    if (errorFieldId) {
        const errorDiv = form.find(`#${errorFieldId}`);
        if (errorDiv.length) {
            errorDiv.addClass('hidden').text('');
        }
    }

    // Reset input field styling
    const inputField = form.find(`[name="${fieldName}"]`);
    if (inputField.length) {
        inputField.removeClass('error border-red-500 focus:ring-red-500 focus:border-red-500');
    }
};

const defaultOptions = {
    onblur: false,
    onfocus: false,
    onfocusin: false,
    onfocusout: false,
    onkeyup: false,
    onclick: false,
    showErrors: showToastErrors
};

// Add custom validation methods
$.validator.addMethod(
    'strongPassword',
    // eslint-disable-next-line no-unused-vars
    (value, _) => {
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])[A-Za-z\d!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]{8,}$/;
        return passwordRegex.test(value);
    },
    '最低8文字以上で、大文字・小文字の英字、数字1文字以上、特殊文字1文字以上を含めてください。'
);

// Add postal code format validation method no required rule needed
$.validator.addMethod(
    'postalCodeFormatNoRequiredRule',
    (value, element) => {
        // Handle composite postal code fields
        if (element.name.startsWith('postal_code') || element.name.startsWith('postalCode')) {
            const form = $(element).closest('form');
            const postalCode1 = (form.find('[name="postal_code1"], [name="postalCode1"]').val() || '').trim();
            const postalCode2 = (form.find('[name="postal_code2"], [name="postalCode2"]').val() || '').trim();

            // Optional composite: both empty → valid (no required rule needed)
            if (postalCode1 === '' && postalCode2 === '') {
                return true;
            }

            // Started filling but only one segment → invalid (must complete XXX + XXXX)
            if (postalCode1 === '' || postalCode2 === '') {
                return false;
            }

            const postalCodeRegex = /^\d+$/;
            const isValid1 = postalCodeRegex.test(postalCode1) && postalCode1.length === 3;
            const isValid2 = postalCodeRegex.test(postalCode2) && postalCode2.length === 4;

            return isValid1 && isValid2;
        }

        // For combined postal code (XXX-XXXX format)
        if (!value || value.trim() === '') {
            return true; // Let required validation handle empty values
        }
        const postalCodeRegex = /^\d{3}-\d{4}$/;
        return postalCodeRegex.test(value.trim());
    },
    '郵便番号は XXX-XXXX の形式で入力してください'
);

// Add postal code format validation method
$.validator.addMethod(
    'postalCodeFormat',
    (value, element) => {
        // Handle composite postal code fields
        if (element.name.startsWith('postal_code') || element.name.startsWith('postalCode')) {
            const form = $(element).closest('form');
            const postalCode1 = form.find('[name="postal_code1"], [name="postalCode1"]').val();
            const postalCode2 = form.find('[name="postal_code2"], [name="postalCode2"]').val();

            // If any part is empty, let required validation handle it
            if (!postalCode1 || !postalCode2) {
                return true;
            }

            // Validate each part has only digits and correct length
            const postalCodeRegex = /^\d+$/;
            const isValid1 = postalCodeRegex.test(postalCode1) && postalCode1.length === 3;
            const isValid2 = postalCodeRegex.test(postalCode2) && postalCode2.length === 4;

            return isValid1 && isValid2;
        }

        // For combined postal code (XXX-XXXX format)
        if (!value || value.trim() === '') {
            return true; // Let required validation handle empty values
        }
        const postalCodeRegex = /^\d{3}-\d{4}$/;
        return postalCodeRegex.test(value.trim());
    },
    '郵便番号は XXX-XXXX の形式で入力してください'
);

// Add kanji format validation method
$.validator.addMethod(
    'kanjiFormat',
    (value, _) => {
        // Allow empty values (let required validation handle it)
        if (!value || value.trim() === '') {
            return true;
        }
        // Kanji characters are in Unicode range U+4E00-U+9FAF, also allow spaces
        const kanjiRegex = /^[\u4E00-\u9FAF\s]+$/;
        return kanjiRegex.test(value.trim());
    },
    '漢字のみで入力してください。'
);

// Add hiragana format validation method
$.validator.addMethod(
    'hiraganaFormat',
    (value, _) => {
        // Allow empty values (let required validation handle it)
        if (!value || value.trim() === '') {
            return true;
        }
        // Hiragana characters are in Unicode range U+3041-U+3096, also allow spaces
        const hiraganaRegex = /^[\u3041-\u3096ー\s]+$/;
        return hiraganaRegex.test(value.trim());
    },
    'ひらがなのみで入力してください。'
);

// Add phone format validation method
$.validator.addMethod(
    'phoneFormat',
    (value, element) => {
        // Handle composite phone fields
        if (element.name.startsWith('phone')) {
            const form = $(element).closest('form');
            const phone1 = form.find('[name="phone1"]').val();
            const phone2 = form.find('[name="phone2"]').val();
            const phone3 = form.find('[name="phone3"]').val();

            // If any part is empty, let required validation handle it
            if (!phone1 || !phone2 || !phone3) {
                return true;
            }

            // Validate each part has only digits
            const phoneRegex = /^\d+$/;
            return phoneRegex.test(phone1) && phoneRegex.test(phone2) && phoneRegex.test(phone3);
        }

        // Handle composite FAX fields
        if (element.name.startsWith('fax')) {
            const form = $(element).closest('form');
            const fax1 = form.find('[name="fax1"]').val();
            const fax2 = form.find('[name="fax2"]').val();
            const fax3 = form.find('[name="fax3"]').val();

            // If any part is empty, let required validation handle it
            if (!fax1 || !fax2 || !fax3) {
                return true;
            }

            // Validate each part has only digits
            const phoneRegex = /^\d+$/;
            return phoneRegex.test(fax1) && phoneRegex.test(fax2) && phoneRegex.test(fax3);
        }

        return true;
    },
    '電話番号の形式が正しくありません。'
);

// Add FAX conditional required validation method
$.validator.addMethod(
    'faxConditionalRequired',
    (value, element) => {
        // Handle composite FAX fields
        if (element.name.startsWith('fax')) {
            const form = $(element).closest('form');
            const fax1 = form.find('[name="fax1"]').val();
            const fax2 = form.find('[name="fax2"]').val();
            const fax3 = form.find('[name="fax3"]').val();

            // Check if any FAX field has a value
            const hasAnyFaxValue = (fax1 && fax1.trim() !== '') || (fax2 && fax2.trim() !== '') || (fax3 && fax3.trim() !== '');

            // If any FAX field has a value, then all FAX fields must have values
            if (hasAnyFaxValue) {
                const currentFieldValue = value ? value.trim() : '';
                return currentFieldValue !== '';
            }

            // If no FAX fields have values, then all are optional
            // Don't clear errors here - let the validation system handle it
            return true;
        }
        return true;
    },
    function () {
        // Get message from settings if available, otherwise use default
        return this.settings.messages[this.name]?.faxConditionalRequired || '正しい形式でFAX番号を入力してください。';
    }
);

// Add Phone conditional required validation method
$.validator.addMethod(
    'phoneConditionalRequired',
    (value, element) => {
        // Handle composite Phone fields
        if (element.name.startsWith('phone')) {
            const form = $(element).closest('form');
            const phone1 = form.find('[name="phone1"]').val();
            const phone2 = form.find('[name="phone2"]').val();
            const phone3 = form.find('[name="phone3"]').val();

            // Check if any Phone field has a value
            const hasAnyPhoneValue = (phone1 && phone1.trim() !== '') || (phone2 && phone2.trim() !== '') || (phone3 && phone3.trim() !== '');

            // If any Phone field has a value, then all Phone fields must have values
            if (hasAnyPhoneValue) {
                const currentFieldValue = value ? value.trim() : '';
                return currentFieldValue !== '';
            }

            // If no Phone fields have values, phone fields are still required by default
            // This method works alongside the 'required' rule
            return true;
        }
        return true;
    },
    '電話番号を入力してください。'
);

// Add dummy validation method for skipErrorHighlight (always passes, used only as flag)
$.validator.addMethod(
    'skipErrorHighlight',
    () => {
        // This is just a flag, always return true
        return true;
    },
    '' // No error message needed
);

// Add custom validation method to handle whitespace-only values for required fields
$.validator.addMethod(
    'requiredNoWhitespace',
    (value, element) => {
        // Check if the field is required
        const isRequired =
            $(element).attr('required') !== undefined || $(element).data('required') === true || $(element).closest('form').data('validator')?.settings?.rules?.[element.name]?.required === true;

        if (!isRequired) {
            return true; // Not required, so always valid
        }

        // For required fields, check if value is empty or only whitespace
        return value && value.trim() !== '';
    },
    'この項目は必須です。'
);

// Add strict email validation method
$.validator.addMethod(
    'strictEmail',
    (value, _element) => {
        // Allow empty values (let required validation handle it)
        if (!value || value.trim() === '') {
            return true;
        }

        // More strict email validation regex
        // Must have @ symbol, domain with at least one dot, and valid characters
        const strictEmailRegex = /^[a-zA-Z0-9._%+-]+@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
        return strictEmailRegex.test(value.trim());
    },
    'メールアドレスの形式が正しくありません。'
);

// Strict URL: http(s) + domain | localhost | IPv4, optional port and path
$.validator.addMethod(
    'strictUrl',
    (value, _element) => {
        if (!value || value.trim() === '') {
            return true;
        }

        const strictUrlRegex = /^(https?:\/\/)((([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)\.)+[a-zA-Z]{2,}|localhost|(\d{1,3}\.){3}\d{1,3})(:\d+)?(\/[^\s]*)?$/;
        return strictUrlRegex.test(value.trim());
    },
    'URLの形式が正しくありません。'
);

// Add alphanumeric validation method
$.validator.addMethod(
    'alphanumeric',
    (value, _element) => {
        // Allow empty values (let required validation handle it)
        if (!value || value.trim() === '') {
            return true;
        }

        // Only alphanumeric characters (a-z, A-Z, 0-9)
        const alphanumericRegex = /^[a-zA-Z0-9]+$/;
        return alphanumericRegex.test(value.trim());
    },
    '半角英数字のみ入力してください。'
);

// Initialize validation for a form
const initValidation = (form, schema, customOptions = {}) => {
    // eslint-disable-next-line no-console
    if (!schema) {
        // eslint-disable-next-line no-console
        console.error('Validation schema is required');
        return null;
    }

    // Extract error display configuration
    const errorDisplayConfig = customOptions.errorDisplay || {};
    const mode = errorDisplayConfig.mode || 'toast'; // default: toast
    const autoClearing = errorDisplayConfig.autoClearing !== false; // default: true
    const onChangeValidation = errorDisplayConfig.onChangeValidation === true; // default: false

    // Create appropriate error handler based on mode
    let showErrorsHandler;
    switch (mode) {
        case 'field':
            showErrorsHandler = (_, errorList) => {
                // Smart error handling: only clear/update errors for fields that are currently being validated
                showFieldErrorsSelectively(form, errorList, schema, errorDisplayConfig);
            };
            break;
        case 'both':
            showErrorsHandler = (_, errorList) => {
                showToastErrors(errorList);
                // Smart error handling: only clear/update errors for fields that are currently being validated
                showFieldErrorsSelectively(form, errorList, schema, errorDisplayConfig);
            };
            break;
        case 'toast':
        default:
            showErrorsHandler = customOptions.showErrors || defaultOptions.showErrors;
            break;
    }

    // Merge default options with schema and custom options
    const options = {
        ...defaultOptions,
        rules: schema.rules,
        messages: schema.messages,
        ...customOptions,
        showErrors: showErrorsHandler
    };

    // Initialize validation
    const validator = form.validate(options);

    // Track validation context to determine if we should clear all errors
    let isFormSubmission = false;

    // Override form submission to flag it
    form.on('submit', () => {
        isFormSubmission = true;
        // Reset flag after a short delay to handle async validation
        setTimeout(() => {
            isFormSubmission = false;
        }, 100);
    });

    // Enhanced showErrors handler that considers context
    if (mode === 'field' || mode === 'both') {
        showErrorsHandler = (validator, errorList) => {
            if (isFormSubmission) {
                // For form submission, clear all errors first
                showFieldErrors(form, errorList, schema, errorDisplayConfig, true);
            } else {
                // For individual field validation, preserve other field errors
                showFieldErrorsSelectively(form, errorList, schema, errorDisplayConfig);
            }

            // Also call toast errors if in 'both' mode
            if (mode === 'both') {
                showToastErrors(errorList);
            }
        };

        // Update validator options with new handler
        validator.settings.showErrors = showErrorsHandler;
    }

    // Add enhanced event listeners for field mode
    if (mode === 'field') {
        form.on('input change', 'input, textarea, select', function () {
            const fieldName = $(this).attr('name');
            if (fieldName) {
                if (onChangeValidation) {
                    // Validate field on change
                    validateSingleField(form, fieldName, validator, schema, errorDisplayConfig);
                } else if (autoClearing) {
                    // Just clear error on change
                    clearFieldError(form, fieldName, schema, errorDisplayConfig);
                }
            }
        });

        if (onChangeValidation) {
            form.on('change', 'input, textarea, select', function () {
                const fieldName = $(this).attr('name');
                if (fieldName) {
                    validateSingleField(form, fieldName, validator, schema, errorDisplayConfig);
                }
            });
        }
    }

    return validator;
};

// Destroy validation
const destroyValidation = form => {
    if (form.data('validator')) {
        form.validate().destroy();
    }
};

/**
 * Postal Code Validation Utilities
 * Centralized utilities for postal code validation and formatting
 */
const PostalCodeValidation = {
    /**
     * Validate postal code format (XXX-XXXX)
     * @param {string} value - The postal code value to validate
     * @returns {boolean} - True if valid format
     */
    validateFormat: value => {
        if (!value || value.trim() === '') {
            return true; // Let required validation handle empty values
        }
        const postalCodeRegex = /^\d{3}-\d{4}$/;
        return postalCodeRegex.test(value.trim());
    },

    /**
     * Format postal code input to XXX-XXXX pattern
     * @param {string} value - The input value to format
     * @returns {string} - Formatted postal code
     */
    formatInput: value => {
        // Remove all non-digit characters
        const digitsOnly = value.replace(/\D/g, '');

        // Limit to 7 digits maximum
        const limitedDigits = digitsOnly.slice(0, 7);

        // Format as XXX-XXXX
        if (limitedDigits.length <= 3) {
            return limitedDigits;
        }
        return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
    },

    /**
     * Validate and auto-correct postal code on blur
     * @param {string} value - The input value
     * @returns {Object} - Validation result with corrected value and error message
     */
    validateAndCorrect: value => {
        const trimmedValue = value.trim();

        // Empty value is valid (let required validation handle it)
        if (!trimmedValue) {
            return { isValid: true, correctedValue: '', errorMessage: null };
        }

        // Check if current value is valid format
        if (PostalCodeValidation.validateFormat(trimmedValue)) {
            return { isValid: true, correctedValue: trimmedValue, errorMessage: null };
        }

        // Try to auto-format if it contains 7 digits
        const digitsOnly = trimmedValue.replace(/\D/g, '');

        if (digitsOnly.length === 7) {
            // Auto-format to XXX-XXXX
            const correctedValue = PostalCodeValidation.formatInput(digitsOnly);
            return { isValid: true, correctedValue, errorMessage: null };
        }

        // Invalid format
        return {
            isValid: false,
            correctedValue: trimmedValue,
            errorMessage: '郵便番号は XXX-XXXX の形式で入力してください'
        };
    }
};

export {
    clearFieldError,
    defaultOptions,
    destroyValidation,
    initValidation,
    PostalCodeValidation,
    showFieldErrors,
    showFieldErrorsSelectively,
    showSingleFieldError,
    showToastErrors,
    validateSingleField
};
